import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, del, get, patch, post, postForm, put, withQuery } from "./client";

/** `/api/v1/inspections`: the field checklist, one document per (inspection,
 * valve). Every write hands back the detail it changed, so a save is one
 * round trip and the screen never shows a stale section. */

/** Which form a visit is filled in on (Phase 1 decision 4): the 33-item PM41
 * inspection, or the PM42 service every new visit defaults to. */
export type InspectionKind = "pm41" | "pm42";

/** How the screens name each form. */
export const KIND_LABEL: Record<InspectionKind, string> = { pm41: "PM41 inspection", pm42: "PM42 service" };

export interface InspectionHead {
  id: number;
  work_order_no: string;
  status: string;
  is_draft: boolean;
  contract_id: number;
  region_id: number | null;
  cluster_id: number | null;
  site_id: number | null;
  chamber_name: string | null;
  site_type: string | null;
  work_type: string | null;
  work_type_label: string | null;
  inspection_date: string | null;
  team_id: number | null;
  team_name: string | null;
  created_at: string | null;
  submitted_at: string | null;
  work_order_from_route: boolean;
  work_order_locked: boolean;
  kind: InspectionKind;
  kind_label: string;
  form_version: string;
  /** The visit header's arrival and departure, on either form. */
  arrived_at?: string | null;
  departed_at?: string | null;
}

export interface InspectionRow extends InspectionHead {
  site: { id: number; name: string } | null;
  valves: number;
  pcd: number | null;
  /** Checks answered across every valve, and asked: the list's meter. */
  answered: number;
  total_checks: number;
}

export interface InspectionRegister {
  rows: InspectionRow[];
  contracts: { id: number; name: string; code: string }[];
  work_types: { value: string; label: string }[];
  filters: { work_type: string | null; q: string | null; kind: string | null };
  office: boolean;
  /** The form Raise offers first: the server's default for a new visit. */
  default_kind: InspectionKind;
  /** The kinds of work that make a visit PM41 work: Raise's form follows them. */
  pm41_work_types: string[];
}

export interface ValveSummary {
  id: number;
  seq: number;
  label: string | null;
  name: string;
  spares: number;
  answered: number;
  total_checks: number;
}

export interface RowFields {
  quantity: string;
  free_issue: string;
  replace: string;
  description: string;
  manufacturer: string;
  part_number: string;
  notes: string;
}

export interface Spare {
  id: number;
  part_description: string;
  manufacturer: string | null;
  part_number: string | null;
  quantity: number;
  free_issue: boolean;
  replace: boolean;
  notes: string | null;
  catalogue_part_id: number | null;
  supplier: string | null;
  size: string | null;
  fields: RowFields;
}

export interface GridPart {
  id: number;
  description: string;
  item_code: string | null;
  active: boolean;
  selling_price: string | null;
  fields: RowFields;
  spare: Spare | null;
}

export interface GridBlock {
  supplier: string;
  size: string;
  parts: GridPart[];
}

export interface Lookup {
  name: string;
  values: string[];
  note: string | null;
  db_backed: boolean;
}

export interface InspectionDetail {
  inspection: InspectionHead;
  site: { id: number; name: string; status: string; notif_no: string | null; rs_no: string | null } | null;
  valves: ValveSummary[];
  active_valve_id: number | null;
  section_two: {
    measurement: {
      measured_chord_mm: number;
      number_of_holes: number;
      gaps_spanned: number;
      calculated_pcd: number;
      formula_version: string;
      valve_face_to_face_mm: number | null;
      pipe_od_mm: number | null;
      pipe_flange_od_mm: number | null;
      pipe_flange_thickness_mm: number | null;
    } | null;
    flange_match: {
      flange_reference_id: number;
      label: string;
      nominal_bore: string;
      matched_pcd: number;
      delta_mm: number;
      method: string;
      direction: string;
      override_reason: string | null;
    } | null;
    populated: Record<string, string | number | null>;
    candidates: { id: number; label: string; delta_mm: number }[];
    unresolved: { kind: string; message: string } | null;
  };
  valve_details: {
    manufacturer: string | null;
    model_no: string | null;
    serial_no: string | null;
    pressure_rating: string | null;
    body_material: string | null;
    flow_direction: string | null;
  };
  pilot: { manufacturer: string | null; model_no: string | null; size: string | null; part_number: string | null };
  qcp: {
    manufacturer: string | null;
    valve_description: string | null;
    dn_scope: number | null;
    dn_boq: number | null;
    equipment_no: string | null;
    functional_location: string | null;
    site_type: string | null;
  };
  spares: {
    rows: Spare[];
    free_text: Spare[];
    manufacturer_default: string | null;
    grid: GridBlock[];
    new_fields: RowFields;
  };
  options: {
    regions: { id: number; name: string }[];
    clusters: { id: number; name: string }[];
    teams: { id: number; name: string }[];
    sites: { id: number; name: string }[];
    work_types: { value: string; label: string }[];
    lookups: Record<"manufacturers" | "pressure_ratings" | "body_materials" | "flow_directions" | "pilot_valve_sizes" | "site_types", Lookup>;
    outcomes: { value: string; label: string }[];
  };
  problems: string[];
  formula_version: string;
  tolerance_mm: number;
  actions: {
    office: boolean;
    can_edit: boolean;
    can_override: boolean;
    can_raise_orders: boolean;
    site_outcome_open: boolean;
    /** The office may move this draft to the other form: nothing is recorded on its own yet. */
    can_change_kind: boolean;
  };
  boq: { id: number; href: string } | null;
  purchase_orders: { id: number; number: string; status: string }[];
  quotable_suppliers: { id: number; name: string }[];
  quotations: { id: number; number: string | null; status: string; supplier_id: number }[];
  unquotable: string[];
  /** The PM42 document, for a PM42 service; its screens are WS2b's. */
  pm42_href?: string | null;
  notice: string | null;
}

export interface CheckItem {
  code: string;
  label: string;
  /** PM41: a tick or yes/no. PM42: a tick for the pre-checks and close-out, pass/fail for the steps. */
  kind: "tick" | "yes_no" | "pass_fail";
  result: string | null;
  notes: string | null;
  /** The number the paper prints ("18", "5A.2"); a PM41 item has none. */
  display_code?: string | null;
  group?: string | null;
  guidance?: string | null;
  if_fail?: string | null;
  photo_role?: string | null;
  /** False where the visit's branches take the item out (PM42, BRS C-26). */
  applicable?: boolean;
  photos?: number;
  performed_at?: string | null;
  initials?: string | null;
}

export interface ChecksSection {
  section: string;
  title: string;
  items: CheckItem[];
  /** Every item is a photograph to take. Absent from an older server: PM41's section 5. */
  photographic?: boolean;
  note?: string | null;
  group_titles?: { group: string; title: string }[];
}

export interface Evidence {
  id: number;
  evidence_type: string;
  check_code: string | null;
  valve_id?: number | null;
  photo_role?: string | null;
  caption: string | null;
  captured_at: string | null;
  url: string;
  kind: "image" | "video" | "pdf";
}

export interface ChecksView {
  inspection: InspectionHead;
  valves: ValveSummary[];
  active_valve_id: number | null;
  sections: ChecksSection[];
  results: string[];
  evidence: Evidence[];
  required_codes: string[];
  /** The photographs a PM42 valve needs (BRS C-27); empty for PM41. */
  required_photos?: { role: string; code: string | null }[];
  is_draft: boolean;
  accepted_types: string[];
}

export const INSPECTIONS_KEY = ["inspections"] as const;
export const inspectionKey = (id: number, valve: number | null) => ["inspections", id, { valve }] as const;
export const checksKey = (id: number, valve: number | null) => ["inspections", id, "checks", { valve }] as const;
/** Every write to one inspection carries this key, so the header can say
 * whether the last one landed without each section reporting it. */
export const saveKey = (id: number) => ["inspections", id, "save"] as const;

export function useInspections(filter: { work_type: string; q: string }) {
  return useQuery<InspectionRegister, ApiError>({
    queryKey: [...INSPECTIONS_KEY, filter],
    queryFn: () => get<InspectionRegister>(withQuery("/api/v1/inspections", filter)),
  });
}

export function useInspection(id: number | null, valve: number | null) {
  return useQuery<InspectionDetail, ApiError>({
    queryKey: inspectionKey(id ?? 0, valve),
    queryFn: () => get<InspectionDetail>(withQuery(`/api/v1/inspections/${id}`, { valve })),
    enabled: id !== null,
  });
}

export function useChecks(id: number | null, valve: number | null) {
  return useQuery<ChecksView, ApiError>({
    queryKey: checksKey(id ?? 0, valve),
    queryFn: () => get<ChecksView>(withQuery(`/api/v1/inspections/${id}/checks`, { valve })),
    enabled: id !== null,
  });
}

/** A write hands back the detail for the valve it touched. The cache is
 * primed for that valve and every other view of the inspection is refetched,
 * because the valve strip and the blockers are shared across valves. */
function useDetailWrite<TVars>(
  id: number,
  write: (vars: TVars) => Promise<InspectionDetail>,
  { refetchList = false }: { refetchList?: boolean } = {},
) {
  const client = useQueryClient();
  return useMutation<InspectionDetail, ApiError, TVars>({
    mutationKey: saveKey(id),
    mutationFn: write,
    onSuccess: (detail) => {
      client.setQueryData(inspectionKey(id, detail.active_valve_id), detail);
      client.setQueryData(inspectionKey(id, null), detail);
      void client.invalidateQueries({ queryKey: [...INSPECTIONS_KEY, id] });
      void client.invalidateQueries({ queryKey: INSPECTIONS_KEY, exact: false, refetchType: refetchList ? "active" : "inactive" });
    },
  });
}

export function useStartInspection() {
  const client = useQueryClient();
  return useMutation<InspectionDetail, ApiError, { contract_id: number; work_order_no: string; kind?: InspectionKind; work_type?: string }>({
    mutationFn: (body) => post<InspectionDetail>("/api/v1/inspections", body),
    onSuccess: (detail) => {
      client.setQueryData(inspectionKey(detail.inspection.id, null), detail);
      void client.invalidateQueries({ queryKey: INSPECTIONS_KEY });
    },
  });
}

export type GeneralIn = Partial<{
  work_order_no: string;
  region_id: number | null;
  cluster_id: number | null;
  site_id: number | null;
  chamber_name: string | null;
  site_type: string | null;
  work_type: string | null;
  inspection_date: string | null;
  team_id: number | null;
  team_name: string | null;
}>;

export function useSaveGeneral(id: number) {
  return useDetailWrite(id, (body: GeneralIn) => patch<InspectionDetail>(`/api/v1/inspections/${id}/general`, body));
}

/** A visit's arrival and departure, through the visit-header endpoint both
 * forms share. It answers in the form's own shape, so the inspection is
 * refetched rather than replaced. */
export function useSaveVisitTimes(id: number) {
  const client = useQueryClient();
  return useMutation<unknown, ApiError, { arrived_at: string | null; departed_at: string | null }>({
    mutationFn: (body) => patch(`/api/v1/inspections/${id}/visit`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: [...INSPECTIONS_KEY, id] });
      void client.invalidateQueries({ queryKey: ["routing"] });
    },
  });
}

export function useAddValve(id: number) {
  return useDetailWrite(id, (label: string | null) => post<InspectionDetail>(`/api/v1/inspections/${id}/valves`, { label }));
}

export function useRenameValve(id: number) {
  return useDetailWrite(id, (vars: { valveId: number; label: string | null }) =>
    patch<InspectionDetail>(`/api/v1/inspections/${id}/valves/${vars.valveId}`, { label: vars.label }),
  );
}

export function useRemoveValve(id: number) {
  return useDetailWrite(id, (valveId: number) => del<InspectionDetail>(`/api/v1/inspections/${id}/valves/${valveId}`));
}

export interface MeasurementIn {
  valve_id: number | null;
  measured_chord_mm: number | null;
  number_of_holes: number | null;
  gaps_spanned: number;
  standard: string | null;
  valve_face_to_face_mm: number | null;
  pipe_od_mm: number | null;
  pipe_flange_od_mm: number | null;
  pipe_flange_thickness_mm: number | null;
}

export function useRecordMeasurement(id: number) {
  return useDetailWrite(id, (body: MeasurementIn) => post<InspectionDetail>(`/api/v1/inspections/${id}/measurement`, body));
}

export function useSelectFlange(id: number) {
  return useDetailWrite(id, (body: { valve_id: number | null; flange_reference_id: number; reason: string | null }) =>
    post<InspectionDetail>(`/api/v1/inspections/${id}/flange`, body),
  );
}

export type ValveDetailsIn = { valve_id: number | null } & Partial<
  Record<
    | "manufacturer"
    | "model_no"
    | "serial_no"
    | "pressure_rating"
    | "body_material"
    | "flow_direction"
    | "pilot_manufacturer"
    | "pilot_model_no"
    | "pilot_size"
    | "pilot_part_number",
    string | null
  >
>;

export function useSaveValveDetails(id: number) {
  return useDetailWrite(id, (body: ValveDetailsIn) => put<InspectionDetail>(`/api/v1/inspections/${id}/valve`, body));
}

export function useSaveSpares(id: number) {
  return useDetailWrite(id, (body: { valve_id: number; form: Record<string, string> }) =>
    put<InspectionDetail>(`/api/v1/inspections/${id}/spares`, body),
  );
}

export function useSubmitInspection(id: number) {
  return useDetailWrite(id, (outcome: string | null) => post<InspectionDetail>(`/api/v1/inspections/${id}/submit`, { outcome }));
}

/** The office moves a blank draft to the other form. The server refuses one that
 * already holds answers, readings, parts or tagged photographs on its form. */
export function useChangeKind(id: number) {
  // The list beside the visit names its form: refetch it now, not after 30 s.
  return useDetailWrite(id, (kind: InspectionKind) => put<InspectionDetail>(`/api/v1/inspections/${id}/kind`, { kind }), {
    refetchList: true,
  });
}

function useChecksWrite<TVars>(id: number, write: (vars: TVars) => Promise<ChecksView>) {
  const client = useQueryClient();
  return useMutation<ChecksView, ApiError, TVars>({
    mutationKey: saveKey(id),
    mutationFn: write,
    onSuccess: (view) => {
      client.setQueryData(checksKey(id, view.active_valve_id), view);
      void client.invalidateQueries({ queryKey: [...INSPECTIONS_KEY, id] });
    },
  });
}

export function useSaveChecks(id: number) {
  return useChecksWrite(id, (body: { valve_id: number | null; answers: Record<string, { result: string | null; notes: string | null }> }) =>
    put<ChecksView>(`/api/v1/inspections/${id}/checks`, body),
  );
}

export function useAddEvidence(id: number) {
  return useChecksWrite(id, (vars: { valveId: number | null; file: File; caption: string; checkCode: string }) => {
    const form = new FormData();
    form.append("upload", vars.file, vars.file.name);
    if (vars.valveId !== null) {
      form.append("valve_id", String(vars.valveId));
    }
    if (vars.caption !== "") {
      form.append("caption", vars.caption);
    }
    if (vars.checkCode !== "") {
      form.append("check_code", vars.checkCode);
    }
    return postForm<ChecksView>(`/api/v1/inspections/${id}/evidence`, form);
  });
}

export const STATUS_LABEL: Record<string, string> = { draft: "Draft", submitted: "Submitted" };

/** The grid a crew edits, as the page posts it: every cell that has a value,
 * named by the field the server told us. A catalogue row with quantity 0 and
 * no ticks is left out, which is what `save_grid` reads as "not needed". */
export function gridForm(cells: Record<string, string>): Record<string, string> {
  const form: Record<string, string> = {};
  for (const [name, value] of Object.entries(cells)) {
    if (value.trim() !== "") {
      form[name] = value.trim();
    }
  }
  return form;
}
